import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, message, Modal } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined } from '@ant-design/icons';
import { useAuthStore } from '../../store/useAuthStore';
import ErrorDisplay from '../common/ErrorDisplay';

const { Title } = Typography;

const LoginForm = () => {
  const [form] = Form.useForm();
  const [forgotPasswordForm] = Form.useForm();
  const { login, loading, error, clearError } = useAuthStore();
  const [validationErrors, setValidationErrors] = useState({});
  const [forgotPasswordModal, setForgotPasswordModal] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);

  const handleSubmit = async (values) => {
    setValidationErrors({});
    clearError();
    
    const result = await login(values.email, values.password);
    
    if (!result.success) {
      if (result.error.includes('validation')) {
        setValidationErrors({ general: result.error });
        message.error(result.error);
      }
    }
  };

  const handleFormChange = () => {
    setValidationErrors({});
    clearError();
  };

  const handleForgotPassword = async (values) => {
    try {
      setForgotPasswordLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: values.email }),
      });

      const data = await response.json();

      if (data.success) {
        setForgotPasswordSent(true);
        message.success('Password reset link sent to your email');
      } else {
        message.error(data.message || 'Failed to send reset link');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      message.error('An error occurred. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const closeForgotPasswordModal = () => {
    setForgotPasswordModal(false);
    setForgotPasswordSent(false);
    forgotPasswordForm.resetFields();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <Card className="shadow-lg">
          <div className="text-center mb-6">
            <Title level={2} className="text-gray-900">
              One Qodequay
            </Title>
            <p className="text-gray-600">
              Sign in to your account to access One Qodequay
            </p>
          </div>

          {error && (
            <div className="mb-4">
              <ErrorDisplay
                errorCode="AUTH_FAILED"
                errorMessage={error}
                closable
                onClose={clearError}
              />
            </div>
          )}

          {validationErrors.general && (
            <div className="mb-4">
              <ErrorDisplay
                errorCode="VALIDATION_ERROR"
                errorMessage={validationErrors.general}
                type="warning"
              />
            </div>
          )}

          <Form
            form={form}
            name="login"
            onFinish={handleSubmit}
            onValuesChange={handleFormChange}
            layout="vertical"
            size="large"
            autoComplete="off"
          >
            <Form.Item
              label="Email"
              name="email"
              rules={[
                {
                  required: true,
                  message: 'Please enter your email!',
                },
                {
                  type: 'email',
                  message: 'Please enter a valid email address!',
                },
                {
                  pattern: /^\S+$/,
                  message: 'Spaces are not allowed in email!',
                },
              ]}
              validateStatus={validationErrors.email ? 'error' : ''}
              help={validationErrors.email}
            >
              <Input
                prefix={<UserOutlined className="text-gray-400" />}
                placeholder="Enter your email"
                autoComplete="email"
                aria-label="Email address"
                className="rounded-md"
              />
            </Form.Item>

            <Form.Item
              label="Password"
              name="password"
              rules={[
                {
                  required: true,
                  message: 'Please enter your password!',
                },
                {
                  min: 6,
                  message: 'Password must be at least 6 characters!',
                },
                {
                  pattern: /^\S+$/,
                  message: 'Spaces are not allowed in password!',
                },
              ]}
              validateStatus={validationErrors.password ? 'error' : ''}
              help={validationErrors.password}
            >
              <Input.Password
                prefix={<LockOutlined className="text-gray-400" />}
                placeholder="Enter your password"
                autoComplete="current-password"
                aria-label="Password"
                className="rounded-md"
              />
            </Form.Item>

            <Form.Item className="mb-2">
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                className="w-full h-12 text-lg font-medium rounded-md bg-blue-600 hover:bg-blue-700 border-blue-600 hover:border-blue-700"
                aria-label="Sign in to One Qodequay"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </Form.Item>

            <Form.Item className="mb-0 text-center">
              <Button 
                type="link" 
                onClick={() => setForgotPasswordModal(true)}
                className="text-blue-600 hover:text-blue-700 p-0"
              >
                Forgot Password?
              </Button>
            </Form.Item>
          </Form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Only registered employees can access One Qodequay.
              <br />
              Contact your administrator if you need access.
            </p>
          </div>
        </Card>
      </div>

      <Modal
        title="Forgot Password"
        open={forgotPasswordModal}
        onCancel={closeForgotPasswordModal}
        footer={null}
        destroyOnClose
      >
        {forgotPasswordSent ? (
          <div className="text-center py-4">
            <div className="text-green-500 text-5xl mb-4">✓</div>
            <h3 className="text-lg font-semibold mb-2">Check Your Email</h3>
            <p className="text-gray-600 mb-4">
              If an account exists with that email address, we've sent a password reset link.
              Please check your inbox and spam folder.
            </p>
            <p className="text-sm text-gray-500 mb-4">
              The link will expire in 1 hour.
            </p>
            <Button type="primary" onClick={closeForgotPasswordModal}>
              Back to Login
            </Button>
          </div>
        ) : (
          <Form
            form={forgotPasswordForm}
            onFinish={handleForgotPassword}
            layout="vertical"
          >
            <p className="text-gray-600 mb-4">
              Enter your email address and we'll send you a link to reset your password.
            </p>
            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Please enter a valid email' },
              ]}
            >
              <Input
                prefix={<MailOutlined className="text-gray-400" />}
                placeholder="Enter your email address"
                size="large"
              />
            </Form.Item>
            <Form.Item className="mb-0">
              <Button
                type="primary"
                htmlType="submit"
                loading={forgotPasswordLoading}
                className="w-full"
                size="large"
              >
                {forgotPasswordLoading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default LoginForm;
